import {
  Config,
  LanguageOptions,
  UnsubscribeWording,
  WithdrawalWording,
} from '@/types';
import {
  SERVICE_TYPES,
  WITHDRAWAL_ONLY_HOSTS,
  WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX,
  type WithdrawalOnlyServiceName,
} from './enums';
import { WITHDRAWAL_WORDING } from '@/wording/withdrawal';

// This is use to apply a specific style for texts when the language have too many characters
// Exemple can be seen in components/Footer.tsx
export const isStringLongerThanMaxLength = (
  string: string,
  maxLength: number,
) => {
  return string.length > maxLength;
};

export const checkUrlCountry = (
  url: string,
  language: string | LanguageOptions,
) => {
  return url.startsWith(language.toLowerCase());
  // FR: This is use to display the download section
  // CY: This is use to change wording language for CY portals and add custom terms text
  // CH: This is use to display text and features for CH portals
};

// This is use to display the account page for fr allopass i+
export const isFRAndAllopassInternetPlus = (url: string) => {
  return [
    'frportal.fitflowclub.net',
    'frportal.pilateshorizon.com',
    'frportal.bodyzenmotion.com',
  ].includes(url);
};

export const capitalizeFirstLetter = (str: string) => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getMaxAgeFromEndDate = (endDateString: string): number => {
  const now = new Date();
  const endDate = new Date(endDateString);

  const diffMs = endDate.getTime() - now.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  return diffSec > 0 ? diffSec : 0;
};

export const formatDateToFrench = (date: string): string => {
  if (!date) return '';

  const [datePart] = date.split(' ');
  const [year, month, day] = datePart.split('-');

  return `${day}/${month}/${year}`;
};

export const shouldShowUnsubscribe = (serviceUrlLink: string) => {
  return (
    (checkUrlCountry(serviceUrlLink, 'ch') ||
      serviceUrlLink === 'bgportal.apptitude.live' ||
      serviceUrlLink === 'skportal.apptitude.live' ||
      serviceUrlLink === 'rsportal2.apptitude.live' ||
      serviceUrlLink === 'rsportal.apptitude.live') &&
    serviceUrlLink !== 'chportal.bodyzenmotion.com'
  );
};

export const shouldShowImpressum = (serviceUrlLink: string) => {
  return (
    checkUrlCountry(serviceUrlLink, 'ch') ||
    checkUrlCountry(serviceUrlLink, 'de')
  );
};

export const shouldShowContact = (serviceUrlLink: string) => {
  return checkUrlCountry(serviceUrlLink, 'de');
};

export const shouldDisplayDate = (config: Config) => {
  if (
    config.serviceType === SERVICE_TYPES.COOKING &&
    checkUrlCountry(config.SERVICE_URL_LINK, 'no')
  ) {
    return false;
  } else {
    return true;
  }
};

export const isWithdrawalOnlyHost = (host: string | null | undefined) => {
  return host ? WITHDRAWAL_ONLY_HOSTS.has(host.toLowerCase()) : false;
};

/** True when the request targets one of the mobile app portals. */
export const isMobileAppPortalHost = (host: string | null | undefined) => {
  const hostname = host?.toLowerCase().replace(/:\d+$/, '');
  return hostname?.startsWith('appportal.') ?? false;
};

/** Restores the viewport to the top after a client-side page transition. */
export const scrollPageToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }
};

export const getValidatedWithdrawalServiceParam = (
  host: string | null | undefined,
  service: string | null | undefined,
) => {
  if (!host || !service) {
    return null;
  }

  const normalizedHost = host.toLowerCase();
  const hostPrefix = normalizedHost.split('portal.')[0];
  const normalizedService = service.trim().toLowerCase();
  const allowedServices: readonly WithdrawalOnlyServiceName[] =
    WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX[
      hostPrefix as keyof typeof WITHDRAWAL_ONLY_SERVICES_BY_HOST_PREFIX
    ] ?? [];

  return (
    allowedServices.find((serviceName) => serviceName === normalizedService) ??
    null
  );
};

export const getWordingUnsubscribe = (
  selectedLanguage: LanguageOptions,
  config?: Pick<
    Config,
    | 'KEYWORD'
    | 'SHORT_CODE'
    | 'SERVICE_CONTACT_EMAIL'
    | 'CUSTOMER_CARE_PHONE_NUMBER'
  >,
): UnsubscribeWording => {
  const wordingUnsubscribe: Partial<
    Record<LanguageOptions, UnsubscribeWording>
  > = {
    FR: {
      successMessage: 'Vous avez été désabonné avec succès.',
      errorMessage: 'Une erreur est survenue.',
      pageTitle: 'Gestion des abonnements',
      phoneInputLabel: 'Entrez votre numéro pour vous désabonner',
      stopInstruction: `Pour résilier votre abonnement, vous pouvez également envoyer un SMS avec le mot-clé : STOP ${config?.KEYWORD} au ${config?.SHORT_CODE}, ou contacter le support par email (${config?.SERVICE_CONTACT_EMAIL}) ou par téléphone (${config?.CUSTOMER_CARE_PHONE_NUMBER}).`,
      confirmButtonLabel: 'Confirmer',
    },
    DE: {
      successMessage: 'Sie wurden erfolgreich abgemeldet.',
      errorMessage: 'Es ist ein Fehler aufgetreten.',
      pageTitle: 'Abo Verwaltung',
      phoneInputLabel: 'Geben Sie Ihre Nummer ein, um sich abzumelden',
      stopInstruction: `Um Ihr Abonnement zu kündigen, können Sie auch eine SMS mit dem Stichwort STOP ${config?.KEYWORD} an die Nummer ${config?.SHORT_CODE} senden oder den Support per E-Mail kontaktieren (${config?.SERVICE_CONTACT_EMAIL}) oder telefonisch (${config?.CUSTOMER_CARE_PHONE_NUMBER}).`,
      confirmButtonLabel: 'Bestätigen',
    },
    BG: {
      successMessage: 'Успешно прекратихте абонамента си.',
      errorMessage: 'Възникна грешка.',
      pageTitle: 'Управление на абонамента',
      phoneInputLabel: 'Въведете номера си, за да се отпишете',
      stopInstruction: `За да прекратите абонамента си, можете също да изпратите SMS с ключова дума STOP APT на номер 14703, или да се свържете с поддръжката по имейл (${config?.SERVICE_CONTACT_EMAIL}).`,
      confirmButtonLabel: 'Потвърди',
    },
    SK: {
      successMessage: 'Boli ste úspešne odhlásený.',
      errorMessage: 'Vyskytla sa chyba.',
      pageTitle: 'Správa predplatného',
      phoneInputLabel: 'Zadajte svoje číslo na odhlásenie',
      stopInstruction: `Ak chcete zrušiť svoje predplatné, môžete tiež kontaktovať podporu e-mailom (${config?.SERVICE_CONTACT_EMAIL}).`,
      confirmButtonLabel: 'Potvrdiť',
    },
    RS: {
      successMessage: 'Vaša pretplata je uspešno otkazana.',
      errorMessage: 'Došlo je do greške prilikom otkazivanja pretplate.',
      pageTitle: 'Upravljanje pretplatom',
      phoneInputLabel: 'Unesite broj telefona za otkazivanje pretplate',
      stopInstruction: `Ako želite da otkažete pretplatu, možete kontaktirati podršku i putem e-pošte (${config?.SERVICE_CONTACT_EMAIL}).`,
      confirmButtonLabel: 'Potvrdi otkazivanje pretplate',
    },
  };

  return wordingUnsubscribe[selectedLanguage]!;
};

export const getWordingWithdrawal = (
  selectedLanguage: LanguageOptions,
): WithdrawalWording => {
  return WITHDRAWAL_WORDING[selectedLanguage];
};
